import React, { useEffect, useMemo, useRef, useState } from "react";

const campanileImg = "/berkeley-campanile-bay-bridge.jpg";
const placements = [
  { firm: "Morgan Stanley", group: "Technology", location: "Menlo Park" },
  { firm: "Morgan Stanley", group: "Generalist", location: "New York" },
  { firm: "Morgan Stanley", group: "Generalist", location: "New York" },
  { firm: "JP Morgan", group: "Generalist", location: "New York" },
  { firm: "Oaktree Capital Management", group: "Private Credit", location: "New York" },
  { firm: "Moelis", group: "Healthcare", location: "San Francisco" },
  { firm: "Bank of America", group: "Generalist", location: "New York" },
  { firm: "Bank of America", group: "EGRC", location: "San Francisco" },
  { firm: "Deutsche Bank", group: "Financial Sponsors", location: "San Francisco" },
  { firm: "LionTree", group: "TMT", location: "San Francisco" },
  { firm: "Jefferies", group: "PCA", location: "New York" },
  { firm: "Alpine Investors", group: "Investing", location: "San Francisco" },
  { firm: "Piper Sandler", group: "Consumer", location: "San Francisco" },
  { firm: "Piper Sandler", group: "Technology", location: "Burlingame" },
  { firm: "Piper Sandler", group: "Oil & Gas", location: "Houston" },
];

const encodeAssetPath = (path) =>
  path
    .split("/")
    .map((segment, index) => (index === 0 && segment === "" ? "" : encodeURIComponent(segment)))
    .join("/");

const heroLogos = [
  { name: "Morgan Stanley", src: "/logos/Morgan_Stanley_Logo_1.svg (1).png", scale: 1.68 },
  { name: "JP Morgan", src: "/logos/J_P_Morgan_Logo_2008_1.svg (1).png", scale: 0.92 },
  { name: "Moelis", src: "/logos/idCijY_s5y_1776793659769.png", scale: 1.5 },
  { name: "Bank of America", src: "/logos/Bank_of_America_logo.svg (1).png", scale: 0.98 },
  { name: "LionTree", src: "/logos/6308.png", scale: 1.08 },
  { name: "Jefferies", src: "/logos/Jefferies_Financial_Group_logo (1).svg", scale: 0.76 },
  { name: "Alpine Investors", src: "/logos/Alpine_Logotype_Positive_Color-1 (1).webp", scale: 0.76 },
  { name: "Deutsche Bank", src: "/logos/Deutsche_Bank_logo.svg.png", scale: 1.08 },
  { name: "Piper Sandler", src: "/logos/updatedlogopiper_1711657442__37599 (1).webp", scale: 1.92 },
  { name: "Oaktree Capital Management", src: "/logos/320px-Oaktree-Capital-Management.svg.png", scale: 1.02 },
];

const differentiators = [
  {
    title: "Office Visits & NY Access",
    body: "BIBW is built around direct New York access, with structured office visits across New York and San Francisco. Members walk into banks, meet real bankers, and build relationships before applications open.",
  },
  {
    title: "Designed around Recruiting",
    body: "Everything in BIBW is built around how investment banking recruiting actually works — real timelines, real outreach, technical preparation that matches what you will face in interviews.",
  },
  {
    title: "Outcome-Focused",
    body: "BIBW is solely focused on helping every member place at the best firm possible. The cohort holds a high standard because everyone is expected to prepare seriously, support the group, and convert.",
  },
];

const values = [
  {
    title: "Relentless Preparation",
    body: "Nothing is given, and nothing is owed. Winning comes from doing more, knowing more, and staying sharper than everyone else.",
  },
  {
    title: "Quiet Confidence",
    body: "Carry ourselves with poise, not ego. Confidence is demonstrated through how you handle pressure and treat people, not through what you tell them about yourself.",
  },
  {
    title: "Shared Standards",
    body: "It's a competitive process, not a selfish one. There is enough food for the whole table. Respect the group, share the win, and never let your ambition come at the expense of others. No toxic culture.",
  },
  {
    title: "Play the Long Game",
    body: "Recruiting is a sprint, but a career is a marathon. Don't burn bridges for a short-term edge, don't take credit that isn't yours, and don't mistake getting the offer for being good at the job. The goal isn't the seat; it's what you do once you're in it. The people in this room are part of that long game, too. The connections made here, built through shared pressure and honest accountability, should last well beyond recruiting.",
  },
];

const curriculumMonths = [
  {
    month: "September",
    title: "Foundations & Industry Overview",
    focus: "Positioning + early recruiting",
    items: [
      "Intro to investment banking, firms, offices, and groups",
      "Resume, recruiting timeline, and positioning",
      "Networking strategy: emails + coffee chats",
      "Behaviorals: TMAY, Why Banking, story building",
      "Submit insight program applications",
    ],
  },
  {
    month: "October",
    title: "Technical Foundations & Coverage Selection",
    focus: "Core finance + choosing your lane",
    items: [
      "Accounting fundamentals and financial statements",
      "Valuation: DCF, trading comps, precedent transactions",
      "Equity value vs. enterprise value",
      "Select industry focus and begin deal research",
    ],
  },
  {
    month: "November",
    title: "Advanced Technicals & Deal Work",
    focus: "Real modeling + deeper understanding",
    items: [
      "Accretion / dilution and merger modeling",
      "LBO modeling introduction",
      "Case-based valuation + real deal walkthroughs",
      "Industry deep dives (Tech, Healthcare, FIG)",
      "Morgan Stanley Technology Case Competition",
    ],
  },
  {
    month: "December",
    title: "Interview Prep & Market Awareness",
    focus: "Converting knowledge → performance",
    items: [
      "Technical interview mastery (accounting, valuation, modeling)",
      "Mock interviews with peers and alumni",
      "Current events + live deal discussions",
    ],
  },
  {
    month: "January",
    title: "Superday Execution",
    focus: "Offer conversion + final push",
    items: [
      "Intensive mock interview cycles",
      "Superday prep and strategy sessions",
      "Final polish across delivery, technicals, and story",
      "Offer phone call preparation, closeout, and final decision support",
    ],
  },
  {
    month: "Winter/Early Spring",
    title: "Placements & Sign Your Offer",
    items: [],
  },
];

const faqItems = [
  {
    question: "Who should apply?",
    answer: "Berkeley sophomores interested in pursuing investment banking or high finance. You don't need to have everything figured out. We're looking for students who are motivated, curious, and willing to put in effort over time.",
  },
  {
    question: "Do I need prior finance or investment banking experience?",
    answer: "No. Many students enter with little to no formal finance experience. The program is designed to build your foundation from the ground up, including technical concepts, recruiting timelines, and how to approach networking. What matters most is your willingness to learn and stay consistent.",
  },
  {
    question: "Is BIBW only for certain majors?",
    answer: "No. Students from a wide range of majors have gone through the program, including non-business and non-technical backgrounds. Investment banking recruiting is much more about preparation and execution than your major.",
  },
  {
    question: "What does the time commitment look like?",
    answer: "The program includes weekly expectations such as workshops, technical preparation, and networking-related tasks. Recruiting for investment banking is time-consuming and requires real consistency outside of class, so students should be prepared to put in meaningful effort over time. The structure of the program is meant to help you use that time well. If you work hard, stay disciplined, and take the process seriously, you will place.",
  },
  {
    question: "How does the application process work?",
    answer: "You'll first submit the application form, which should only take a few minutes to complete. After that, applications are reviewed, and selected applicants may be contacted for an interview or follow-up conversation. Final cohort decisions are then made based on overall fit, interest, and commitment to the program.",
  },
  {
    question: "Is there a cost to participate?",
    answer: "If admitted, participation in BIBW is free. Optional experiences, such as office visits or external events, may involve additional costs for students who choose to take part.",
  },
  {
    question: "How are applications reviewed?",
    answer: "Applications are reviewed on a rolling basis, so applying early gives you the best chance of being considered. Every application is read by the full leadership team — there is no single gatekeeper. We look at the whole picture: your interest in the program, your commitment level, and how seriously you are approaching recruiting. We are not looking for the most polished applicant. We are looking for the most coachable and committed one.",
  },
];

const leadershipTeam = [
  { role: "Co-President", name: "Anamika Chawla", email: "anamika.chawla@berkeley.edu" },
  { role: "Co-President", name: "Caden Fuchs", email: "caden_fuchs@berkeley.edu" },
  { role: "Head of Recruitment", name: "Willem Berry", email: "willemberry@berkeley.edu" },
  { role: "Head of Recruitment", name: "Jasmine Wells", email: "jasmine_wells@berkeley.edu" },
  { role: "Head of Standards", name: "Everest Schipper", email: "everest_schipper@berkeley.edu" },
  { role: "Head of Standards", name: "Khalid Farah", email: "khalidfarah@berkeley.edu" },
  { role: "Head of Curriculum", name: "Nicholas Spano", email: "nspano@berkeley.edu" },
  { role: "Head of Curriculum", name: "Aaron Agarwal", email: "aaron_agarwal@berkeley.edu" },
  { role: "Head of Programming", name: "Kylan Foss", email: "kylanfoss@berkeley.edu" },
  { role: "Head of Programming", name: "Rushil Mohan", email: "rushilmohan@berkeley.edu" },
];

const neutralSectionBg = "#F4F5F7";
const surfacePanelStyle = {
  background: "#fff",
  border: "1px solid var(--line-soft)",
  borderRadius: 8,
  boxShadow: "var(--shadow-sm)",
};
const reportPanelStyle = {
  ...surfacePanelStyle,
  boxShadow: "var(--shadow)",
};

const getGmailComposeUrl = (email) =>
  `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;

function PageShell({ children }) {
  return (
    <div style={{ maxWidth: 1152, margin: "0 auto", padding: "0 var(--page-gutter)" }}>
      {children}
    </div>
  );
}

function FullPageShell({ children }) {
  return (
    <div style={{ width: "100%", padding: "0 var(--page-gutter)" }}>
      {children}
    </div>
  );
}

function SectionHeader({ eyebrow, title, body, center = false, titleStyle }) {
  return (
    <div style={{ maxWidth: 760, margin: center ? "0 auto 2.2rem" : "0 0 2.2rem", textAlign: center ? "center" : "left" }}>
      <span className="section-eyebrow">{eyebrow}</span>
      <span className="gold-line" style={{ marginLeft: center ? "auto" : 0, marginRight: center ? "auto" : 0 }} />
      <h2 className="section-title" style={{ marginTop: "1rem", ...titleStyle }}>{title}</h2>
      {body && <p style={{ marginTop: "0.9rem", color: "var(--body)", lineHeight: 1.78, fontSize: "1rem", maxWidth: 680 }}>{body}</p>}
    </div>
  );
}

function MarqueeBar() {
  // Triple the content so there's always content filling both sides during the loop
  const logos = [...heroLogos, ...heroLogos, ...heroLogos];

  return (
    <div className="marquee-shell">
      <div className="marquee-track marquee-track-logos">
        {logos.map((logo, index) => {
          const logoUrl = encodeAssetPath(logo.src);

          return (
            <React.Fragment key={`${logo.name}-${index}`}>
              <span className="marquee-logo-item">
                <span className="marquee-logo-frame">
                  <span
                    className="marquee-logo-mark"
                    role="img"
                    aria-label={logo.name}
                    title={logo.name}
                    style={{
                      "--logo-scale": logo.scale ?? 1,
                      "--logo-shift-x": logo.shiftX ?? "0px",
                      "--logo-shift-y": logo.shiftY ?? "0px",
                      WebkitMaskImage: `url("${logoUrl}")`,
                      maskImage: `url("${logoUrl}")`,
                    }}
                  />
                </span>
              </span>
              <span className="marquee-logo-separator" aria-hidden="true" />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function HoverCard({ children, style }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="card"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        boxShadow: hover ? "var(--shadow)" : "0 1px 2px rgba(0,0,0,0.04)",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition: "box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease",
        borderColor: hover ? "rgba(29,53,87,0.28)" : "var(--line)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, type = "text", placeholder }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.5rem", minWidth: 0 }}>
      <span style={{ color: "var(--ink)", fontSize: "0.86rem", fontWeight: 750 }}>{label}</span>
      <input
        type={type}
        placeholder={placeholder}
        style={{
          height: 46,
          borderRadius: 8,
          border: "1px solid var(--line)",
          background: "var(--surface)",
          color: "var(--ink)",
          padding: "0 0.875rem",
          outline: "none",
          fontSize: "0.92rem",
        }}
      />
    </label>
  );
}

function SelectField({ label, options }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.5rem", minWidth: 0 }}>
      <span style={{ color: "var(--ink)", fontSize: "0.86rem", fontWeight: 750 }}>{label}</span>
      <select
        style={{
          height: 46,
          borderRadius: 8,
          border: "1px solid var(--line)",
          background: "var(--surface)",
          color: "var(--ink)",
          padding: "0 0.875rem",
          outline: "none",
          fontSize: "0.92rem",
        }}
      >
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

function DiffCard({ item }) {
  return (
    <HoverCard style={{
      ...surfacePanelStyle,
      padding: "1.95rem 1.7rem 1.6rem",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
      boxShadow: "var(--shadow-sm)",
    }}>
      <div>
        <div style={{ width: 38, height: 2, background: "rgba(201,162,39,0.82)", marginBottom: "1.2rem" }} />
        <h3 style={{ margin: 0, color: "var(--ink)", fontSize: "1.16rem", fontWeight: 880, lineHeight: 1.22, letterSpacing: "-0.02em" }}>{item.title}</h3>
        <p style={{ margin: "0.82rem 0 0", color: "var(--body)", fontSize: "1.05rem", lineHeight: 1.78 }}>{item.body}</p>
      </div>
      {item.footer && (
        <div style={{ marginTop: "1.4rem", paddingTop: "0.95rem", borderTop: "1px solid rgba(15,23,42,0.07)" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 820, color: "var(--campus)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{item.footer}</span>
        </div>
      )}
    </HoverCard>
  );
}

function HomePage({ goToPage }) {
  const heroBorder = "2px solid rgba(255,255,255,0.46)";

  return (
    <>
      {/* ── Hero + firm ticker: exact first viewport ─────────────── */}
      <section className="hero-shell" style={{ minHeight: "calc(100vh - 64px)", background: "#0D1E30", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div className="hero-main" style={{ display: "flex", flex: 1, minHeight: 0, borderBottom: heroBorder }}>
          {/* Left — text panel */}
          <div className="hero-copy" style={{ flex: "0 0 43%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "3.2rem 2.65rem 3.15rem var(--page-gutter)", position: "relative", zIndex: 2 }}>
            <div className="anim-fade-up section-eyebrow">
              Berkeley Investment Banking Workshop BIBW
            </div>
            <h1 className="anim-fade-up d1 hero-title hero-heading" style={{ marginTop: "0.95rem", textAlign: "left", maxWidth: 560 }}>
              15/15 Placed. Berkeley’s Pipeline to Wall Street.
            </h1>
            <div className="anim-fade-up d2 hero-gold-line" style={{ marginTop: "1.25rem" }}>
              <div style={{ width: 44, height: 2, background: "var(--gold)" }} />
            </div>
            <p className="anim-fade-up d2 hero-subtext" style={{ marginTop: "1rem", fontSize: "0.95rem", lineHeight: 1.7, color: "rgba(255,255,255,0.72)", maxWidth: 495 }}>
              An outcomes-driven workshop preparing Berkeley students to compete for and land top investment banking roles across New York and San Francisco.
            </p>
            <div className="anim-fade-up d3 hero-actions" style={{ marginTop: "1.5rem", display: "flex", gap: "0.82rem", flexWrap: "wrap" }}>
              <button className="btn-gold" onClick={() => goToPage("apply")}>Apply Now</button>
              <button className="btn-hero-outline" onClick={() => goToPage("placements")}>View Placements</button>
            </div>
          </div>

          {/* Right — image */}
          <div className="hero-media" style={{
            flex: "0 0 57%",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#0D1E30",
            backgroundImage: `url(${campanileImg})`,
            backgroundSize: "cover",
            backgroundPosition: "42% center",
            backgroundRepeat: "no-repeat",
            borderLeft: heroBorder,
          }}>
            <img
              className="hero-image"
              src={campanileImg}
              alt="UC Berkeley Campanile at sunset"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "42% center",
                display: "block",
                zIndex: 0,
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, rgba(13,30,48,0.06), rgba(13,30,48,0.01) 24%, rgba(13,30,48,0.01)), linear-gradient(180deg, rgba(15,23,42,0), rgba(15,23,42,0.08))",
                zIndex: 1,
              }}
            />
          </div>
        </div>

        <div className="hero-ticker" style={{ background: "#0D1E30", padding: "1rem 0 1.1rem", flexShrink: 0 }}>
          <MarqueeBar />
        </div>
      </section>

      {/* ── What is BIBW ──────────────────────────────────────────── */}
      <section className="what-section" style={{ padding: "var(--section-space) 0", minHeight: "calc(100vh - 64px)", background: neutralSectionBg, display: "flex", alignItems: "center" }}>
        <div style={{ width: "100%", padding: "0 var(--page-gutter)" }}>
          <div className="what-grid" style={{ display: "grid", gridTemplateColumns: "42fr 58fr", gap: "4.6rem", alignItems: "center" }}>
            <div className="what-copy">
              <span className="section-eyebrow">BIBW Overview</span>
              <span className="gold-line" />
              <h2 className="section-title" style={{ marginTop: "1rem", fontSize: "clamp(2.45rem, 3.35vw, 3.05rem)", lineHeight: 1.04, maxWidth: 520, letterSpacing: "-0.035em" }}>What is BIBW?</h2>
              <p style={{ marginTop: "1.42rem", color: "var(--body)", lineHeight: 1.8, fontSize: "1.02rem", maxWidth: 510 }}>
                BIBW is a <strong style={{ color: "var(--ink)", fontWeight: 850 }}>cohort program</strong> focused on investment banking recruiting at Berkeley. It is peer-led, alumni backed and built to develop students into candidates who can place. We operate around the idea that if you work hard and stay committed, you will get a job in investment banking in the city that you want.
              </p>
              <p style={{ marginTop: "1.05rem", color: "var(--body)", lineHeight: 1.8, fontSize: "1.02rem", maxWidth: 510 }}>
                You <strong style={{ color: "var(--ink)", fontWeight: 850 }}>don’t need to come in polished</strong>. The point is to grow through structure, feedback, technical reps, outreach discipline, and a serious peer standard.
              </p>
            </div>

            <div className="what-card" style={{ ...reportPanelStyle, padding: "2.15rem 2.35rem 2.05rem", boxShadow: "var(--shadow)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "baseline", paddingBottom: "1.25rem", borderBottom: "1px solid var(--line)" }}>
                <span className="section-eyebrow">Development model</span>
                <span style={{ color: "var(--muted)", fontSize: "0.74rem", fontWeight: 800 }}>Structure over polish</span>
              </div>
              <div>
                {[
                  "Find students who are committed to the entire investment banking recruitment cycle.",
                  "Build the technical base and recruiting habits.",
                  "Put students in rooms, conversations, and timelines that matter.",
                  "Convert preparation into interviews, offers, and long-term momentum.",
                ].map((line, i) => (
                  <div key={line} style={{ display: "grid", gridTemplateColumns: "2.7rem 1fr", gap: "1rem", alignItems: "start", padding: "1.18rem 0", borderBottom: i === 3 ? "none" : "1px solid var(--line)" }}>
                    <span style={{ color: "rgba(154,116,22,0.78)", fontSize: "0.72rem", fontWeight: 900, letterSpacing: "0.08em", paddingTop: "0.22rem" }}>
                      0{i + 1}
                    </span>
                    <span style={{ color: "var(--body)", lineHeight: 1.72, fontSize: "0.96rem" }}>{line}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ────────────────────────────────────────────────── */}
      <section className="values-section" style={{ padding: "var(--section-space-tight) 0", minHeight: "calc(100vh - 64px)", background: "#0D1E30", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center" }}>
        <div style={{ width: "100%", padding: "0 var(--page-gutter)" }}>
          <div className="values-grid" style={{ display: "grid", gridTemplateColumns: "35fr 65fr", gap: "3.85rem", alignItems: "start" }}>
            <div className="values-intro" style={{ position: "sticky", top: 96, paddingTop: "1.6rem" }}>
              <span className="section-eyebrow">BIBW Mission &amp; Values</span>
              <span className="gold-line" />
              <h2 className="section-title" style={{ marginTop: "1.25rem", color: "#fff", fontSize: "clamp(2.2rem, 3.45vw, 3.2rem)", lineHeight: 1.08, maxWidth: 480, letterSpacing: "-0.025em" }}>
                Standards behind the outcomes
              </h2>
              <p style={{ margin: "1.45rem 0 0", color: "rgba(255,255,255,0.68)", fontSize: "0.98rem", lineHeight: 1.82, maxWidth: 430 }}>
                We empower students from all backgrounds to secure investment banking offers by upholding an exceptional standard of{" "}
                <span style={{ color: "rgba(201,162,39,0.78)", fontWeight: 800 }}>preparation</span>,{" "}
                <span style={{ color: "rgba(201,162,39,0.78)", fontWeight: 800 }}>character</span>, and{" "}
                <span style={{ color: "rgba(201,162,39,0.78)", fontWeight: 800 }}>consistency</span>.
              </p>
            </div>

            <div className="values-list" style={{ position: "relative", paddingLeft: "2.05rem", borderLeft: "1px solid rgba(201,162,39,0.24)" }}>
              {values.map((item, i) => (
                <div className="values-row" key={item.title} style={{ position: "relative", padding: i === 0 ? "0 0 2.1rem" : "2.1rem 0", borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.10)" }}>
                  <div className="values-number" style={{ position: "absolute", left: "-2.95rem", top: i === 0 ? 2 : "2.25rem", color: "rgba(201,162,39,0.62)", background: "#0D1E30", padding: "0.15rem 0", fontSize: "0.72rem", fontWeight: 900, letterSpacing: "0.08em" }}>
                    0{i + 1}
                  </div>
                  <div style={{ color: "#fff", fontSize: "1.18rem", fontWeight: 900, lineHeight: 1.18, letterSpacing: "-0.015em" }}>
                    {item.title}
                  </div>
                  <p style={{ margin: "0.62rem 0 0", color: "rgba(255,255,255,0.64)", fontSize: "0.95rem", lineHeight: 1.8, maxWidth: 860 }}>
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Differentiators ───────────────────────────────────────── */}
      <section className="why-section" style={{ padding: "var(--section-space) 0 var(--section-space-tight)", background: neutralSectionBg, borderTop: "1px solid var(--line)" }}>
        <div style={{ width: "100%", padding: "0 var(--page-gutter)" }}>

          {/* Intro */}
          <div style={{ maxWidth: 760, marginBottom: "2.35rem" }}>
            <span className="section-eyebrow">Why BIBW</span>
            <span className="gold-line" />
            <h2 className="section-title" style={{ marginTop: "0.95rem" }}>Engineered to get the offer.</h2>
            <p style={{ marginTop: "0.9rem", color: "var(--body)", fontSize: "1rem", lineHeight: 1.8 }}>
              The workshop is built for one outcome: securing the seat.
            </p>
          </div>

          {/* 3-column cards */}
          <div className="diff-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.2rem" }}>
            {differentiators.map((item) => (
              <DiffCard key={item.title} item={item} />
            ))}
          </div>

          {/* Proof strip */}
          <div className="proof-strip" style={{ marginTop: "2.5rem", paddingTop: "1.85rem", borderTop: "1px solid var(--line)", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.1rem", alignItems: "start" }}>
            {[
              { value: "SF + NY", label: "Office Exposure" },
              { value: "15 / 15", label: "Placed · Cohort I" },
              { value: "6", label: "Bulge Bracket Placements · Cohort I" },
            ].map((stat) => (
              <div key={stat.label} style={{ borderLeft: "1px solid var(--line)", paddingLeft: "1.25rem" }}>
                <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "2.18rem", fontWeight: 700, color: "var(--campus)", lineHeight: 1, letterSpacing: "-0.02em" }}>{stat.value}</div>
                <div style={{ marginTop: "0.3rem", fontSize: "0.7rem", fontWeight: 780, color: "var(--muted)", letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1.35 }}>{stat.label}</div>
              </div>
            ))}
          </div>

        </div>
      </section>
    </>
  );
}

function PlacementsPage() {
  const locationDistribution = [
    { label: "New York", count: 6, percent: 40, note: "Primary recruiting hub", color: "var(--gold)" },
    { label: "San Francisco", count: 6, percent: 40, note: "Technology & West Coast coverage", color: "var(--campus)" },
    { label: "Other", count: 3, percent: 20, note: "Silicon Valley Tech and Oil & Gas groups", color: "var(--muted)" },
  ];

  const logoByName = Object.fromEntries(heroLogos.map((l) => [l.name, l]));

  // Per-logo: scale = visual size multiplier, offsetX = px shift left to correct for
  // internal whitespace inside the logo file that pushes the brand mark rightward after scaling.
  const placementLogoStyle = {
    "Morgan Stanley":             { scale: 2.05, offsetX: -11 },
    "JP Morgan":                  { scale: 1.18, offsetX: 0 },
    "Moelis":                     { scale: 1.85, offsetX: -9 },
    "Bank of America":            { scale: 0.98, offsetX: 0 },
    "LionTree":                   { scale: 1.60, offsetX: 0 },
    "Jefferies":                  { scale: 0.88, offsetX: 0 },
    "Alpine Investors":           { scale: 0.88, offsetX: 0 },
    "Deutsche Bank":              { scale: 1.58, offsetX: 0 },
    "Piper Sandler":              { scale: 2.20, offsetX: 0 },
    "Oaktree Capital Management": { scale: 1.42, offsetX: 0 },
  };

  const stats = [
    { value: "100%", label: "Placement Rate" },
    { value: "7", label: "Bulge Brackets" },
    { value: "6 / 15", label: "New York" },
  ];

  return (
    <FullPageShell>
      <section className="placements-section" style={{ padding: "1.45rem 0 5.15rem", minHeight: "calc(100vh - 64px)" }}>

        {/* Header */}
        <div style={{ marginBottom: "1.1rem" }}>
          <span className="section-eyebrow">BIBW Outcomes · Cohort I</span>
          <span className="gold-line" />
          <h2 className="section-title" style={{ marginTop: "0.95rem", color: "var(--ink)" }}>
            BIBW Placements
          </h2>
        </div>

        {/* Stats strip */}
        <div className="placements-stats-strip" style={{ display: "flex", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", marginBottom: "1.75rem" }}>
          {stats.map((s, i) => (
            <div key={s.label} style={{ flex: 1, padding: "1rem 1.5rem", borderRight: i < stats.length - 1 ? "1px solid var(--line)" : "none" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(1.4rem, 2.4vw, 1.85rem)", fontWeight: 700, color: "var(--campus)", lineHeight: 1, letterSpacing: "-0.02em" }}>{s.value}</div>
              <div style={{ marginTop: "0.28rem", fontSize: "0.68rem", fontWeight: 850, textTransform: "uppercase", letterSpacing: "0.11em", color: "var(--muted)" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Placement grid */}
        <div className="placements-grid">
          {placements.map((item, index) => {
            const logo = logoByName[item.firm];
            const { scale, offsetX } = placementLogoStyle[item.firm] ?? { scale: 1, offsetX: 0 };
            const transformStr = offsetX !== 0
              ? `translateX(${offsetX}px) scale(${scale})`
              : `scale(${scale})`;
            return (
              <div key={`${item.firm}-${index}`} className="placement-logo-card">
                <div className="placement-logo-frame">
                  {logo && (
                    <img
                      src={encodeAssetPath(logo.src)}
                      alt={item.firm}
                      className="placement-logo-img"
                      style={{ transform: transformStr, transformOrigin: "left center" }}
                    />
                  )}
                </div>
                <div className="placement-logo-meta">
                  <span className="placement-logo-group">{item.group}</span>
                  <span className="placement-logo-dot" aria-hidden="true" />
                  <span className="placement-logo-location" data-ny={item.location === "New York"}>{item.location}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Location mix — secondary, bottom left */}
        <div style={{ marginTop: "4rem", paddingTop: "2.5rem", borderTop: "1px solid var(--line)", maxWidth: 560 }}>
          <span className="section-eyebrow" style={{ display: "block", marginBottom: "0.5rem" }}>BIBW Location Mix</span>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem", marginTop: "1rem" }}>
            {locationDistribution.map((item) => (
              <div key={item.label}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.38rem" }}>
                  <div>
                    <span style={{ color: "var(--ink)", fontSize: "0.84rem", fontWeight: 800 }}>{item.label}</span>
                    <span style={{ marginLeft: "0.6rem", color: "var(--muted)", fontSize: "0.72rem", fontWeight: 600 }}>{item.note}</span>
                  </div>
                  <span style={{ color: "var(--muted)", fontSize: "0.76rem", fontWeight: 750, whiteSpace: "nowrap" }}>{item.count} ({item.percent}%)</span>
                </div>
                <div className="placement-bar-track">
                  <div className="placement-bar-fill" style={{ width: `${item.percent}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

      </section>
    </FullPageShell>
  );
}

function CurriculumPage({ goToPage }) {
  const rowRefs = useRef([]);
  const [activeMonth, setActiveMonth] = useState(0);

  /* ── FINALE POPUP (saved, currently disabled) ──────────────────────────
  const finaleRef = useRef(null);
  const [finaleActive, setFinaleActive] = useState(false);

  useEffect(() => {
    if (!finaleRef.current || typeof IntersectionObserver === "undefined") return undefined;
    let timeout;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          timeout = setTimeout(() => setFinaleActive(true), 600);
        }
      },
      { threshold: 0, rootMargin: "0px 0px -40% 0px" }
    );
    observer.observe(finaleRef.current);
    return () => { observer.disconnect(); clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    if (!finaleActive) return;
    let locked = true;
    let scrolledToTop = false;
    const lockTimer = setTimeout(() => { locked = false; }, 3000);
    const handleScroll = () => {
      if (locked || scrolledToTop) return;
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 80;
      if (atBottom) {
        scrolledToTop = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => setFinaleActive(false), 900);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => { window.removeEventListener("scroll", handleScroll); clearTimeout(lockTimer); };
  }, [finaleActive]);
  ── END FINALE POPUP ───────────────────────────────────────────────────── */

  useEffect(() => {
    const nodes = rowRefs.current.filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visible.length) return;

        const nextIndex = Number(visible[0].target.getAttribute("data-index"));
        if (!Number.isNaN(nextIndex)) setActiveMonth(nextIndex);
      },
      {
        threshold: [0.35, 0.5, 0.7],
        rootMargin: "-12% 0px -24% 0px",
      }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="curriculum-section" style={{ padding: "1.45rem 0 5.2rem", minHeight: "calc(100vh - 64px)", background: "var(--bg)" }}>
      <div style={{ width: "100%", padding: "0 var(--page-gutter)" }}>
        <div className="curriculum-shell">
          <div className="curriculum-heading-block">
            <span className="section-eyebrow">BIBW Curriculum</span>
            <span className="gold-line" />
            <h2 className="section-title" style={{ marginTop: "0.9rem", color: "var(--ink)", maxWidth: 860 }}>
              From Day One to Signing Your Offer.
            </h2>
          </div>

          <div className="curriculum-timeline">
            <div className="curriculum-timeline-line" aria-hidden="true" />
            {curriculumMonths.map((item, index) => {
              const state =
                index === activeMonth ? "active" : index < activeMonth ? "past" : "upcoming";
              const isOfferPhase = index === curriculumMonths.length - 1;

              return (
              <div
                key={item.month}
                ref={(node) => { rowRefs.current[index] = node; }}
                data-index={index}
                className={`curriculum-timeline-row ${state}`}
              >
                <div className="curriculum-timeline-left">
                  <div className="curriculum-timeline-month">{item.month}</div>
                  <h3 className="curriculum-timeline-title">{item.title}</h3>
                </div>
                <div className="curriculum-timeline-marker" aria-hidden="true">
                  <span />
                </div>
                <div className="curriculum-timeline-right">
                  {isOfferPhase ? (
                    <div className="curriculum-offer-content" style={{ display: "flex", alignItems: "center", marginTop: "-0.12rem", alignSelf: "stretch", paddingLeft: "1rem" }}>
                      <button
                        className="btn-navy"
                        onClick={() => goToPage("apply")}
                        style={{ fontSize: "0.84rem", fontWeight: 760, letterSpacing: "0.02em", padding: "0 2.8rem", height: "clamp(3.6rem, 5.4vw, 4.1rem)", minHeight: "unset", borderRadius: 8, lineHeight: 1, whiteSpace: "nowrap" }}
                      >Apply to BIBW</button>
                    </div>
                  ) : (
                    item.items.map((detail) => (
                      <div key={detail} className="curriculum-timeline-item">
                        {detail}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );})}
          </div>
        </div>
      </div>
      {/* ── FINALE POPUP (saved, currently disabled) ──────────────────────
      <div className={`curriculum-inline-finale${finaleActive ? " finale-active" : ""}`} aria-hidden={!finaleActive}>
        <div className="curriculum-inline-finale-inner">
          <div className="curriculum-payoff-label">Final Step</div>
          <span className="gold-line" />
          <div className="curriculum-finale-title">Sign your offer</div>
          <div className="curriculum-finale-actions">
            <button className="btn-navy curriculum-finale-btn" onClick={() => goToPage("apply")}>Apply to BIBW</button>
            <button className="curriculum-finale-back" onClick={() => { setFinaleActive(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Back to top</button>
          </div>
        </div>
      </div>
      ── END FINALE POPUP ─────────────────────────────────────────────── */}
    </section>
  );
}

function LeadershipPage() {
  return (
    <section className="leadership-section" style={{ padding: "1.45rem 0 2.5rem", minHeight: "calc(100vh - 64px)", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
      {/* Eyebrow + title — own shell, untouched */}
      <FullPageShell>
        <div style={{ maxWidth: 760, marginBottom: "1.75rem" }}>
          <span className="section-eyebrow">Leadership</span>
          <span className="gold-line" />
          <h2 className="section-title" style={{ marginTop: "0.95rem", color: "var(--ink)" }}>
            BIBW Leadership Team
          </h2>
        </div>
      </FullPageShell>

      {/* Rows — fully independent, centered horizontally and vertically in remaining space */}
      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
      <div className="leadership-rows-inner" style={{ maxWidth: 960, width: "100%", margin: "0 auto", padding: "0 var(--page-gutter) 0 calc(var(--page-gutter) + 2.5rem)" }}>
        {leadershipTeam.map((member) => (
          <div
            key={`${member.role}-${member.name}`}
            className="leadership-row"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(180px, 1fr) minmax(160px, 1fr) minmax(160px, 1fr)",
              gap: "1.15rem",
              alignItems: "baseline",
              padding: "0.92rem 0",
              borderBottom: "1px solid var(--line)",
            }}
          >
            <div style={{ color: "var(--ink)", fontSize: "0.77rem", fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", lineHeight: 1.4 }}>
              {member.role}
            </div>
            <div style={{ color: "var(--body)", fontSize: "0.96rem", fontWeight: 760, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
              {member.name}
            </div>
            <a
              href={getGmailComposeUrl(member.email)}
              target="_blank"
              rel="noreferrer"
              style={{
                color: "var(--body)",
                fontSize: "0.86rem",
                fontWeight: 680,
                lineHeight: 1.5,
                textDecoration: "none",
              }}
            >
              {member.email}
            </a>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

function FieldHelper({ children }) {
  if (!children) return null;
  return <span style={{ color: "var(--muted)", fontSize: "0.76rem", lineHeight: 1.45 }}>{children}</span>;
}

function TextAreaField({ label, placeholder, required, helper, name }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.32rem", gridColumn: "1 / -1" }}>
      <span style={{ color: "var(--ink)", fontSize: "0.86rem", fontWeight: 750 }}>
        {label}{required && <span style={{ color: "var(--gold-deep)", marginLeft: 3 }}>*</span>}
      </span>
      <FieldHelper>{helper}</FieldHelper>
      <textarea
        name={name}
        placeholder={placeholder}
        rows={2}
        style={{
          borderRadius: 8,
          border: "1px solid rgba(226,232,240,0.95)",
          background: "#FCFDFE",
          color: "var(--ink)",
          padding: "0.75rem 0.875rem",
          outline: "none",
          fontSize: "0.92rem",
          resize: "vertical",
          fontFamily: "inherit",
        }}
      />
    </label>
  );
}

function LabeledField({ label, children, required, span, helper }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.32rem", minWidth: 0, gridColumn: span ? "1 / -1" : undefined }}>
      <span style={{ color: "var(--ink)", fontSize: "0.86rem", fontWeight: 750 }}>
        {label}{required && <span style={{ color: "var(--gold-deep)", marginLeft: 3 }}>*</span>}
      </span>
      <FieldHelper>{helper}</FieldHelper>
      {children}
    </label>
  );
}

const inputStyle = {
  height: 44, borderRadius: 8, border: "1px solid rgba(226,232,240,0.95)",
  background: "#FCFDFE", color: "var(--ink)",
  padding: "0 0.875rem", outline: "none", fontSize: "0.92rem", fontFamily: "inherit",
};

function FaqAccordion({ item, open, onToggle }) {
  return (
    <div className={`faq-item${open ? " faq-open" : ""}`}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: "1rem 0.95rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1.25rem",
          cursor: "pointer",
          textAlign: "left",
          color: open ? "var(--campus)" : "var(--ink)",
        }}
      >
        <span style={{ fontSize: "0.97rem", lineHeight: 1.45, fontWeight: 820 }}>{item.question}</span>
        <span style={{ width: 26, height: 26, borderRadius: 8, border: open ? "1px solid rgba(29,53,87,0.24)" : "1px solid var(--line)", background: open ? "#fff" : "var(--surface)", color: open ? "var(--campus)" : "var(--muted)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 850 }}>
          {open ? "−" : "+"}
        </span>
      </button>
      <div
        style={{
          maxHeight: open ? 800 : 0,
          opacity: open ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 220ms ease, opacity 180ms ease",
        }}
      >
        <p style={{ margin: "0 0.95rem 0.95rem", color: "var(--body)", fontSize: "0.9rem", lineHeight: 1.72 }}>
          {item.answer}
        </p>
      </div>
    </div>
  );
}

const APPLICATION_RECIPIENT_EMAIL = "willemberry@berkeley.edu";
const APPLY_FORM_ENDPOINT = `https://formsubmit.co/${APPLICATION_RECIPIENT_EMAIL}`;

const getApplyReturnUrl = () => {
  if (typeof window === "undefined") return "https://berkeleyibworkshop.com/apply?submitted=true";
  return `${window.location.origin}/apply?submitted=true`;
};

function SuccessModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(15,23,42,0.38)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: "2.2rem 2.4rem 2rem",
          maxWidth: 420,
          width: "100%",
          boxShadow: "0 24px 60px rgba(15,23,42,0.14)",
          position: "relative",
        }}
      >
        <div style={{ width: 28, height: 1.5, background: "var(--gold)", marginBottom: "1.2rem" }} />
        <div style={{ color: "var(--campus)", fontSize: "1.22rem", fontWeight: 880, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          Application Received
        </div>
        <p style={{ margin: "0.85rem 0 0", color: "var(--body)", fontSize: "0.9rem", lineHeight: 1.72 }}>
          Your application has been successfully submitted. Our team will review all applications carefully. Selected candidates will be contacted with next steps.
        </p>
        <p style={{ margin: "0.75rem 0 0", color: "var(--muted)", fontSize: "0.84rem", lineHeight: 1.6 }}>
          Have questions? Reach out directly at{" "}
          <a href={`mailto:${APPLICATION_RECIPIENT_EMAIL}`} style={{ color: "var(--campus)", fontWeight: 750, textDecoration: "none" }}>
            {APPLICATION_RECIPIENT_EMAIL}
          </a>
        </p>
        <button
          onClick={onClose}
          style={{
            marginTop: "1.6rem",
            width: "100%",
            background: "var(--campus)",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            padding: "0.65rem 1rem",
            fontSize: "0.82rem",
            fontWeight: 800,
            cursor: "pointer",
            letterSpacing: "0.02em",
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

function ApplyPage() {
  const [openFaq, setOpenFaq] = useState(-1);
  const [missingFields, setMissingFields] = useState([]);
  const [resumeFileName, setResumeFileName] = useState("");
  const [showSubmitted, setShowSubmitted] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("submitted") === "true"
  );

  const requiredFieldLabels = {
    fullName: "Full name",
    email: "Berkeley Email",
    year: "Class Year",
    major: "Major(s) / Minor(s)",
    location: "Preferred office",
    resume: "Resume",
  };

  const isMissing = (field) => missingFields.includes(field);
  const clearFieldError = (field) => {
    setMissingFields((current) => current.filter((item) => item !== field));
  };

  const handleApplySubmit = (event) => {
    const formData = new FormData(event.currentTarget);
    const resumeFile = formData.get("attachment");
    const nextMissingFields = [];

    ["fullName", "email", "year", "major", "location"].forEach((field) => {
      const value = `${formData.get(field) || ""}`.trim();
      if (!value) nextMissingFields.push(field);
    });

    if (!resumeFile || !resumeFile.name) {
      nextMissingFields.push("resume");
    }

    if (nextMissingFields.length > 0) {
      event.preventDefault();
      setMissingFields(nextMissingFields);
    }
  };

  return (
    <div style={{ background: "var(--bg)" }}>
      {showSubmitted && (
        <SuccessModal
          onClose={() => {
            setShowSubmitted(false);
            window.history.replaceState({}, "", "/apply");
          }}
        />
      )}
      <FullPageShell>
        <section className="apply-section" style={{ padding: "1.45rem 0 5.1rem", minHeight: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>
          <SectionHeader
            eyebrow="BIBW Apply"
            title="BIBW Application"
            titleStyle={{ color: "var(--ink)" }}
          />

          <div className="apply-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,60fr) minmax(320px,40fr)", gap: "1.55rem", alignItems: "stretch", marginTop: "-0.35rem" }}>
            <form
              className="apply-form-card"
              noValidate
              action={APPLY_FORM_ENDPOINT}
              method="POST"
              encType="multipart/form-data"
              onSubmit={handleApplySubmit}
              style={{ ...reportPanelStyle, borderTop: "2px solid var(--campus)", padding: "1.5rem 1.5rem 1rem", height: "100%", boxShadow: "var(--shadow)", display: "flex", flexDirection: "column" }}
            >
              <input type="hidden" name="_subject" value="New BIBW Application" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value={getApplyReturnUrl()} />
              <div style={{ marginBottom: "0.75rem" }}>
                <h3 style={{ margin: "0 0 0", color: "var(--ink)", fontSize: "1.48rem", lineHeight: 1.12, fontWeight: 850 }}>Apply Now</h3>
              </div>
              <p style={{ margin: "0 0 0.75rem", color: "var(--body)", lineHeight: 1.68, fontSize: "0.9rem" }}>
                We encourage anyone interested to apply. Applications are reviewed on a rolling basis.
              </p>

              {missingFields.length > 0 && (
                <div style={{ margin: "0 0 0.95rem", padding: "0.8rem 0.9rem", borderRadius: 8, border: "1px solid rgba(185,28,28,0.16)", background: "rgba(185,28,28,0.04)", color: "#991b1b" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 820, lineHeight: 1.45 }}>
                    Please complete: {missingFields.map((field) => requiredFieldLabels[field]).join(", ")}.
                  </div>
                </div>
              )}

              <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem 1rem", flex: 1 }}>
                <LabeledField label="Full name" required>
                  <input
                    name="fullName"
                    type="text"
                    placeholder="Jane Smith"
                    onChange={() => clearFieldError("fullName")}
                    style={{ ...inputStyle, borderColor: isMissing("fullName") ? "#dc2626" : inputStyle.border.split(" ").slice(-1)[0] }}
                  />
                </LabeledField>

                <LabeledField label="Berkeley Email" required>
                  <input
                    name="email"
                    type="email"
                    placeholder="janesmith@berkeley.edu"
                    onChange={() => clearFieldError("email")}
                    style={{ ...inputStyle, borderColor: isMissing("email") ? "#dc2626" : inputStyle.border.split(" ").slice(-1)[0] }}
                  />
                </LabeledField>

                <LabeledField label="Class Year" required>
                  <select
                    name="year"
                    onChange={() => clearFieldError("year")}
                    style={{ ...inputStyle, borderColor: isMissing("year") ? "#dc2626" : inputStyle.border.split(" ").slice(-1)[0] }}
                  >
                    <option value="">Select year</option>
                    {["2027", "2028", "2029", "2030"].map(y => <option key={y}>{y}</option>)}
                  </select>
                </LabeledField>

                <LabeledField label="Major(s) / Minor(s)" required>
                  <input
                    name="major"
                    type="text"
                    placeholder="Economics, Statistics"
                    onChange={() => clearFieldError("major")}
                    style={{ ...inputStyle, borderColor: isMissing("major") ? "#dc2626" : inputStyle.border.split(" ").slice(-1)[0] }}
                  />
                </LabeledField>

                <LabeledField label="Preferred office" required>
                  <div style={{ minHeight: 44, display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "wrap", border: `1px solid ${isMissing("location") ? "#dc2626" : "rgba(226,232,240,0.95)"}`, borderRadius: 8, padding: "0.42rem 0.45rem", background: "#FCFDFE" }}>
                    {["New York", "San Francisco", "Open"].map(loc => (
                      <label className="office-choice" key={loc} style={{ cursor: "pointer", fontSize: "0.84rem", color: "var(--body)", fontWeight: 650 }}>
                        <input
                          type="radio"
                          name="location"
                          value={loc}
                          onChange={() => clearFieldError("location")}
                          style={{ accentColor: "var(--campus)" }}
                        />
                        <span>{loc}</span>
                      </label>
                    ))}
                  </div>
                </LabeledField>

                <LabeledField label="Resume" required>
                  <label style={{ cursor: "pointer" }}>
                    <div style={{ minHeight: 44, borderRadius: 8, border: `1.5px dashed ${isMissing("resume") ? "#dc2626" : "rgba(29,53,87,0.18)"}`, background: "rgba(241,245,249,0.82)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", padding: "0 0.85rem", color: resumeFileName ? "var(--body)" : "var(--muted)", fontSize: "0.84rem", transition: "border-color 180ms" }}>
                      <span style={{ fontWeight: 750, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {resumeFileName || "Upload resume"}
                      </span>
                      <span style={{ color: "var(--campus)", fontWeight: 850 }}>PDF</span>
                    </div>
                    <input
                      name="attachment"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        setResumeFileName(file?.name || "");
                        clearFieldError("resume");
                      }}
                    />
                  </label>
                </LabeledField>

                <LabeledField label="LinkedIn URL" span>
                  <input name="linkedin" type="url" placeholder="https://linkedin.com/in/yourname" style={inputStyle} />
                </LabeledField>

                <LabeledField label="GPA">
                  <input name="gpa" type="text" placeholder="3.7" style={inputStyle} />
                </LabeledField>

                <LabeledField label="Referral / How did you hear about BIBW">
                  <input name="referral" type="text" placeholder="Friend, Instagram, class announcement" style={inputStyle} />
                </LabeledField>

                <TextAreaField name="notes" label="Anything else we should know?" placeholder="Share anything relevant that did not fit above." />
              </div>

              <button
                type="submit"
                className="btn-navy"
                style={{ marginTop: "1.25rem", width: "100%", borderRadius: 8, fontSize: "0.92rem" }}
              >
                Submit Application
              </button>
            </form>

            <div className="apply-faq-card" style={{ ...reportPanelStyle, borderTop: "2px solid var(--gold)", padding: "1.5rem 1.42rem 1rem", height: "100%", display: "flex", flexDirection: "column", boxShadow: "var(--shadow)" }}>
              <h3 style={{ margin: "0 0 0", color: "var(--ink)", fontSize: "1.65rem", lineHeight: 1.12, fontWeight: 850 }}>Frequently Asked Questions</h3>
              <p style={{ margin: "0.7rem 0 0.82rem", color: "var(--muted)", lineHeight: 1.68, fontSize: "0.92rem", maxWidth: 420 }}>
                Quick answers before you apply.
              </p>
              <div style={{ flex: 1 }}>
                {faqItems.map((item, index) => (
                  <FaqAccordion
                    key={item.question}
                    item={item}
                    open={openFaq === index}
                    onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
                  />
                ))}
              </div>
              <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid var(--line)" }}>
                <div style={{ color: "var(--ink)", fontSize: "0.95rem", fontWeight: 850 }}>Questions before applying?</div>
                <a href={`mailto:${APPLICATION_RECIPIENT_EMAIL}`} style={{ display: "inline-block", marginTop: "0.35rem", color: "var(--campus)", fontSize: "0.9rem", fontWeight: 750, textDecoration: "none" }}>
                  {APPLICATION_RECIPIENT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </section>
      </FullPageShell>
    </div>
  );
}

export default function App() {
  const nav = [
    { key: "home", label: "Home", mobileLabel: "Home", href: "/" },
    { key: "curriculum", label: "Curriculum", mobileLabel: "Program", href: "/curriculum" },
    { key: "leadership", label: "Leadership", mobileLabel: "Team", href: "/leadership" },
    { key: "placements", label: "Placements", mobileLabel: "Outcomes", href: "/placements" },
    { key: "apply", label: "Apply", mobileLabel: "Apply", href: "/apply" },
  ];

  const validPages = useMemo(() => new Set(nav.filter((item) => !item.external).map((item) => item.key)), [nav]);
  const getInitialPage = () => {
    if (typeof window === "undefined") return "home";
    const slug = window.location.pathname.replace(/^\//, "").split("/")[0];
    return slug && validPages.has(slug) ? slug : "home";
  };

  const [page, setPage] = useState(getInitialPage);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const titles = {
      home: "BIBW Home | Berkeley Investment Banking Workshop",
      curriculum: "BIBW Curriculum | Berkeley Investment Banking Workshop",
      leadership: "BIBW Leadership | Berkeley Investment Banking Workshop",
      placements: "BIBW Placements | Berkeley Investment Banking Workshop",
      apply: "BIBW Apply | Berkeley Investment Banking Workshop",
    };

    document.title = titles[page] || titles.home;
  }, [page]);

  useEffect(() => {
    const onPop = () => {
      const slug = window.location.pathname.replace(/^\//, "").split("/")[0];
      setPage(slug && validPages.has(slug) ? slug : "home");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [validPages]);

  const goToPage = (key) => {
    setPage(key);
    setMobileNavOpen(false);
    if (typeof window !== "undefined") {
      const path = key === "home" ? "/" : `/${key}`;
      window.history.pushState({}, "", path);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <header className="site-header" style={{ position: "sticky", top: 0, zIndex: 40, background: "rgba(248,250,252,0.9)", borderBottom: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 10px 28px rgba(15,23,42,0.035)", backdropFilter: "blur(18px)" }}>
        <div className="site-header-inner" style={{ maxWidth: "none", margin: 0, padding: "0.76rem var(--page-gutter)", minHeight: 64, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          {/* Brand + desktop nav row */}
          <div className="site-brand-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1.05rem", flexShrink: 0, width: "100%" }}>
            <a
              className="site-logo"
              href="/"
              onClick={(event) => {
                event.preventDefault();
                goToPage("home");
              }}
              style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0, flexShrink: 0, textDecoration: "none" }}
            >
              <span className="site-logo-mark" style={{ display: "block", color: "var(--campus)", fontSize: "1.26rem", fontWeight: 920, letterSpacing: "-0.045em", lineHeight: 0.95 }}>
                BIBW
              </span>
              <span className="site-logo-line" aria-hidden="true" style={{ display: "block", width: 34, height: 1, background: "var(--gold)", marginTop: "0.24rem" }} />
            </a>

            {/* Desktop nav */}
            <nav className="site-nav site-nav-desktop" style={{ display: "flex", alignItems: "center", gap: "1.55rem", flexWrap: "wrap" }}>
              {nav.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(event) => {
                    if (item.external) return;
                    event.preventDefault();
                    goToPage(item.key);
                  }}
                  className={`nav-link${page === item.key ? " active" : ""}`}
                  aria-current={!item.external && page === item.key ? "page" : undefined}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "none" }}
                >
                  <span className="nav-label-desktop">{item.label}</span>
                </a>
              ))}
            </nav>

            {/* Mobile hamburger button */}
            <button
              className="mobile-hamburger"
              aria-label="Toggle navigation"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((o) => !o)}
              style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: "0.25rem", flexShrink: 0 }}
            >
              <span className="hamburger-icon" aria-hidden="true">
                {mobileNavOpen ? "✕" : "☰"}
              </span>
            </button>
          </div>

          {/* Mobile dropdown nav */}
          <nav className={`site-nav-mobile${mobileNavOpen ? " open" : ""}`} aria-hidden={!mobileNavOpen}>
            {nav.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(event) => {
                  if (item.external) return;
                  event.preventDefault();
                  goToPage(item.key);
                }}
                className={`nav-link mobile-nav-link${page === item.key ? " active" : ""}`}
                aria-current={!item.external && page === item.key ? "page" : undefined}
                style={{ textDecoration: "none" }}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {page === "home" && <HomePage goToPage={goToPage} />}
      {page === "placements" && <PlacementsPage />}
      {page === "curriculum" && <CurriculumPage goToPage={goToPage} />}
      {page === "leadership" && <LeadershipPage />}
      {page === "apply" && <ApplyPage />}

      <footer className="site-footer" style={{ borderTop: "1px solid rgba(226,232,240,0.95)", background: "rgba(248,250,252,0.9)", boxShadow: "0 -10px 28px rgba(15,23,42,0.035)", backdropFilter: "blur(18px)" }}>
        <div
          className="site-footer-inner"
          style={{
            maxWidth: "none",
            margin: 0,
            padding: "1.2rem var(--page-gutter) 1rem",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1.25rem",
          }}
        >
          <div style={{ flex: "1 1 440px", maxWidth: 700 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", color: "var(--campus)", lineHeight: 1.05, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ color: "var(--campus)", fontSize: "1.26rem", fontWeight: 920, letterSpacing: "-0.045em", lineHeight: 0.95 }}>
                  BIBW
                </span>
                <span aria-hidden="true" style={{ display: "block", width: 34, height: 1, background: "var(--gold)", marginTop: "0.24rem" }} />
              </span>
              <span style={{ color: "rgba(29,53,87,0.42)" }}>|</span>
              <a
                href={`mailto:${APPLICATION_RECIPIENT_EMAIL}`}
                style={{
                  color: "var(--campus)",
                  fontSize: "0.86rem",
                  fontWeight: 820,
                  textDecoration: "none",
                }}
              >
                {APPLICATION_RECIPIENT_EMAIL}
              </a>
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.55rem 1rem", flex: "1 1 260px", justifyContent: "flex-end", flexWrap: "wrap", paddingTop: "0.08rem", alignItems: "center" }}>
            {nav.filter((item) => !item.external).map((item) => (
              <a
                key={`footer-${item.key}`}
                href={item.href}
                onClick={(event) => {
                  event.preventDefault();
                  goToPage(item.key);
                }}
                style={{ color: "var(--body)", fontSize: "0.84rem", fontWeight: 680, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
